import { computed, Injectable, signal } from '@angular/core';
import { taskTokenizer } from '../store/token';
import { injectDispatch } from '@ngrx/signals/events';
import { TASK_EVENTS } from '../store';
import { TaskInstance, TaskListType, TaskStatus } from '../types';
import { MarkStatus } from '@submissions/v1/models';
import { Section } from '@data-access/shared';

@Injectable({
  providedIn: 'root',
})
export class TasksFacadeService {
  private readonly _tasksSignalStore = taskTokenizer();
  private _taskEvents = injectDispatch(TASK_EVENTS);

  /*  Signals */
  $documentTasks = this._tasksSignalStore.$documentTasks;
  $error = this._tasksSignalStore.$error;
  $isLoading = this._tasksSignalStore.$isLoading;
  $tasks = this._tasksSignalStore.$tasks;
  $filtered = this._tasksSignalStore.$filtered;
  $threads = this._tasksSignalStore.$threads;
  $isSMEQCLoading = this._tasksSignalStore.$isSMEQCLoading;
  /*  Signals */

  /** Computed Properties */

  /** A list of tasks associated to a 'question' entity */
  $questionTasks = this._tasksSignalStore.$questionTasks;
  /** A list of tasks associated to a 'section' entity */
  $sectionTasks = this._tasksSignalStore.$sectionTasks;
  /** A list of tasks associated to a 'document' SME QC entity, that are internal and not completed */
  $remainingDocumentSMEQCTasks =
    this._tasksSignalStore.$remainingDocumentSMEQCTasks;
  /** A list of tasks associated to a 'question' SME QC entity, that are internal and not completed */
  $remainingQuestionSMEQCTasks =
    this._tasksSignalStore.$remainingQuestionSMEQCTasks;
  /** A list of tasks associated to a 'section' entity, that are internal and not completed */
  $remainingSectionSMEQCTasks =
    this._tasksSignalStore.$remainingSectionSMEQCTasks;
  /** A list of all other collabhub tasks that are available for both internal and external users */
  $remainingToDoCollabHubTasks =
    this._tasksSignalStore.$remainingToDoCollabHubTasks;
  /** A Map of remaining document tasks (type 'document') */
  $remainingDocumentToDoTasks =
    this._tasksSignalStore.$remainingDocumentToDoTasks;
  /** A Map of remaining requested document tasks */
  $remainingRequestedDocumentToDoTasks =
    this._tasksSignalStore.$remainingRequestedDocumentToDoTasks;
  /** A Map of remaining question tasks */
  $remainingQuestionToDoTasks =
    this._tasksSignalStore.$remainingQuestionToDoTasks;
  /** A Map of remaining section tasks */
  $remainingSectionToDoTasks =
    this._tasksSignalStore.$remainingSectionToDoTasks;

  /** A todo count of all remaining tasks for submission document for external users */
  $remainingDocumentTaskCount =
    this._tasksSignalStore.$remainingToDoDocumentTaskCount;
  /** A todo count of all remaining tasks for submission details for external users */
  $remainingToDoDetailTaskCount =
    this._tasksSignalStore.$remainingToDoDetailTaskCount;
  /** A todo count of all remaining tasks for submission sections for external users */
  $remainingSectionTaskTodoCount =
    this._tasksSignalStore.$remainingSectionTaskTodoCount;

  $qSections = signal<Section[]>([]);
  $collabHubHistoryVisible = signal(false);

  qSections = computed(() => {
    const _sections = this.$qSections();
    return _sections;
  });

  /** Computed Properties */

  toggleCollabHubHistory(): void {
    this.$collabHubHistoryVisible.update((prev) => !prev);
  }

  updateTaskList(taskList: TaskInstance[]) {
    this.updateTaskListFilteredState(true);
    this._taskEvents.patchTaskList({ taskList });
  }

  clearTaskEntities() {
    this._taskEvents.patchTaskList({
      taskList: [],
    });
  }

  clearTaskList(): void {
    this._taskEvents.resetTaskListState();
  }

  /*
   * Load task list for a given workspace ID and submission stage
   * @param workspaceID - The ID of the workspace
   * @param submissionStage - The current stage of the submission
   */
  loadTaskList(workspaceID: string, submissionStage: string) {
    this.updateTaskListFilteredState(false);
    this._taskEvents.getTaskList({
      submissionStage,
      workspaceID,
    });
  }

  loadSmeQcTaskList(workspaceId: string, isCompleted?: boolean) {
    this._taskEvents.getSMEQCTaskList({
      isCompleted: isCompleted,
      taskType: ['sme_mark_required', 'qc_mark_required'],
      workspaceId,
    });
  }

  /*
   * Update the filtered state of the task list
   * @param filtered - Boolean indicating if the task list is filtered
   */
  updateTaskListFilteredState(filtered: boolean) {
    this._taskEvents.updateTaskListFiltered({ filtered });
  }

  /*
   * @NOTE: Update SME/QC mark status for a specific section within a workspace
   * */
  updateSMEQCMark(
    workspaceId: string,
    sectionId: string,
    markStatus: MarkStatus,
  ) {
    this._taskEvents.updateSMEQCMARK({ markStatus, sectionId, workspaceId });
  }

  /*
   * @NOTE: Update individual task instance status basing on task-instanceId and workspaceId
   * */
  updateTaskStatus(
    workspaceId: string,
    taskInstanceId: string,
    taskStatus: TaskStatus,
    taskListType: TaskListType,
  ): void {
    this._taskEvents.updateTaskStatus({
      taskInstanceId,
      taskListType,
      taskStatus,
      workspaceId,
    });
  }

  /*
   * Update the status of multiple task instances
   * @NOTE: Bulk update task instance status basing on task-instanceIds and workspaceId
   * */
  bulkUpdateTaskStatus(
    workspaceId: string,
    taskInstanceIds: string[],
    taskStatus: TaskStatus,
    taskListType: TaskListType,
  ): void {
    this._taskEvents.bulkUpdateTaskStatus({
      taskInstanceIds,
      taskListType,
      taskStatus,
      workspaceId,
    });
  }

  /*
   * Export all task threads for a given workspace ID
   * @param workspaceID - The ID of the workspace
   */
  exportAllThreads(workspaceID: string) {
    this._taskEvents.exportAllThreads({ workspaceID });
  }
}
